import { motion } from "framer-motion";
import { Mail, Phone } from "lucide-react";
import BackgroundFX from "@/components/BackgroundFX";
import AnimatedLogo from "@/components/AnimatedLogo";
import Countdown from "@/components/Countdown";
import ContactForm from "@/components/ContactForm";

const EMAIL = "hola@xperimentad.com";
const PHONE_DISPLAY = "55 6749 5806";
const WHATSAPP = "525567495806"; // +52 México

const CREDITS = ["Tecnología", "Arte", "Diseño"];

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
});

export default function Landing() {
  return (
    <div className="relative min-h-screen text-white">
      <BackgroundFX />

      <div className="relative z-10">
        {/* Header */}
        <header
          data-testid="site-header"
          className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-12"
        >
          <motion.div
            {...fadeUp(0)}
            className="flex items-center gap-3"
            data-testid="header-brand"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_startup-forge-114/artifacts/vlayxu0a_23Recurso%202.png"
              alt="Xperimentad"
              className="h-9 w-9 object-contain"
            />
            <span className="hidden font-mono text-sm uppercase tracking-[0.3em] text-white/80 sm:block">
              Xperimentad
            </span>
          </motion.div>

          <motion.div {...fadeUp(0.1)} className="flex items-center gap-2 sm:gap-3">
            <a
              data-testid="header-email-link"
              href={`mailto:${EMAIL}`}
              className="hidden items-center gap-2 rounded-full border border-white/[0.12] px-4 py-2 font-mono text-xs text-white/80 backdrop-blur-md transition-all hover:border-[#00F0FF]/60 hover:text-[#00F0FF] sm:flex"
            >
              <Mail className="h-3.5 w-3.5" />
              {EMAIL}
            </a>
            <a
              data-testid="header-whatsapp-link"
              href={`https://wa.me/${WHATSAPP}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-full border border-white/[0.15] bg-transparent px-4 py-2 font-mono text-xs text-white backdrop-blur-md transition-all hover:border-[#00F0FF] hover:text-[#00F0FF]"
            >
              <WhatsAppIcon className="h-4 w-4" />
              <span className="hidden sm:inline">WhatsApp</span>
            </a>
          </motion.div>
        </header>

        {/* Hero */}
        <main className="mx-auto max-w-7xl px-6 lg:px-12">
          <section className="flex flex-col items-center pt-10 text-center sm:pt-16">
            <motion.div {...fadeUp(0.1)}>
              <AnimatedLogo size={260} />
            </motion.div>

            <motion.div
              {...fadeUp(0.25)}
              className="mt-8 flex items-center justify-center"
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 sm:text-xs">
                Próximamente
              </span>
            </motion.div>

            <motion.h1
              {...fadeUp(0.35)}
              className="mt-6 max-w-4xl text-5xl font-light tracking-tighter text-white sm:text-6xl md:text-7xl lg:text-8xl"
            >
              Estamos construyendo
              <br />
              <span className="bg-gradient-to-r from-white via-white to-[#00F0FF]/80 bg-clip-text text-transparent">
                algo extraordinario
              </span>
            </motion.h1>

            {/* Carrusel estilo créditos encima del texto */}
            <div className="mt-8 w-full" data-testid="credits-section">
              <div className="credits-mask relative mx-auto h-40 max-w-md overflow-hidden sm:h-48">
                <div className="credits-roll flex flex-col items-center text-center">
                  {[...CREDITS, ...CREDITS].map((word, i) => (
                    <span
                      key={i}
                      data-testid={`credit-${i}`}
                      className="py-2 text-4xl font-light tracking-tight text-white/90 sm:text-5xl md:text-6xl"
                    >
                      {word}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <motion.p
              {...fadeUp(0.45)}
              className="mt-4 max-w-2xl text-xl leading-relaxed text-gray-300 sm:text-2xl md:text-3xl"
            >
              Únete a la lista de espera y obtén un descuento sobre tu proyecto.
            </motion.p>
          </section>

          {/* Countdown */}
          <section className="mx-auto mt-16 max-w-3xl" data-testid="countdown-section">
            <motion.p
              {...fadeUp(0.15)}
              className="mb-5 text-center font-mono text-xs uppercase tracking-[0.3em] text-[#00F0FF]/70"
            >
              El futuro arranca en
            </motion.p>
            <Countdown />
          </section>

          {/* Contact */}
          <section
            id="contacto"
            className="mt-20 grid grid-cols-1 gap-8 lg:grid-cols-12"
            data-testid="contact-section"
          >
            <motion.div {...fadeUp(0.1)} className="lg:col-span-7">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00F0FF]/70">
                Hablemos
              </p>
              <h2 className="mt-4 text-3xl font-light tracking-tight text-white sm:text-4xl lg:text-5xl">
                ¿Te unes a cambiar el rumbo?
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-gray-400">
                Escríbenos por correo, whatsapp, nuestro equipo te contactará.
              </p>

              <div className="mt-8 space-y-3">
                <a
                  data-testid="contact-email-direct"
                  href={`mailto:${EMAIL}`}
                  className="group flex items-center gap-4 border border-white/[0.08] bg-white/[0.015] p-4 backdrop-blur-md transition-colors hover:border-[#00F0FF]/40"
                >
                  <span className="flex h-10 w-10 items-center justify-center border border-white/[0.1] text-[#00F0FF]">
                    <Mail className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500">
                      Correo
                    </p>
                    <p className="text-sm text-white">{EMAIL}</p>
                  </div>
                </a>
                <a
                  data-testid="contact-whatsapp-direct"
                  href={`https://wa.me/${WHATSAPP}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 border border-white/[0.08] bg-white/[0.015] p-4 backdrop-blur-md transition-colors hover:border-[#00F0FF]/40"
                >
                  <span className="flex h-10 w-10 items-center justify-center border border-white/[0.1] text-[#00F0FF]">
                    <WhatsAppIcon className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500">
                      WhatsApp · Teléfono
                    </p>
                    <p className="text-sm text-white">{PHONE_DISPLAY}</p>
                  </div>
                </a>
              </div>
            </motion.div>

            <div className="lg:col-span-5">
              <ContactForm />
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer
          data-testid="site-footer"
          className="mx-auto mt-24 max-w-7xl border-t border-white/[0.06] px-6 py-8 lg:px-12"
        >
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p className="font-mono text-xs text-gray-600">
              © {new Date().getFullYear()} Xperimentad. Todos los derechos
              reservados.
            </p>
            <div className="flex items-center gap-5">
              <a
                href={`mailto:${EMAIL}`}
                className="text-gray-500 transition-colors hover:text-[#00F0FF]"
                aria-label="Email"
              >
                <Mail className="h-4 w-4" />
              </a>
              <a
                href={`https://wa.me/${WHATSAPP}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 transition-colors hover:text-[#00F0FF]"
                aria-label="WhatsApp"
              >
                <WhatsAppIcon className="h-4 w-4" />
              </a>
              <a
                href={`tel:+${WHATSAPP}`}
                className="text-gray-500 transition-colors hover:text-[#00F0FF]"
                aria-label="Teléfono"
              >
                <Phone className="h-4 w-4" />
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
